/**
 * Custom Date Range Modal for SimpleTable datetime filters
 *
 * Allows users to select custom date ranges for datetime filtering
 */

import React, { useState, useEffect } from "react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { ResponsiveModal } from "@wtc/ui";
import { format, parseISO, isValid } from "date-fns";
import { Calendar, X } from "lucide-react";

interface DateTimeRangeModalProps {
  isOpen: boolean;
  onClose: () => void;
  onApply: (start: Date, end: Date) => void;
  fieldLabel: string;
  initialStart?: Date;
  initialEnd?: Date;
}

export const DateTimeRangeModal: React.FC<DateTimeRangeModalProps> = ({
  isOpen,
  onClose,
  onApply,
  fieldLabel,
  initialStart,
  initialEnd,
}) => {
  const [startDate, setStartDate] = useState<Date>(initialStart || new Date());
  const [endDate, setEndDate] = useState<Date>(initialEnd || new Date());
  const [startDateInput, setStartDateInput] = useState<string>("");
  const [endDateInput, setEndDateInput] = useState<string>("");
  const [errors, setErrors] = useState<{ start?: string; end?: string }>({});

  // Initialize inputs when modal opens
  useEffect(() => {
    if (isOpen) {
      const start = initialStart || new Date();
      const end = initialEnd || new Date();
      setStartDate(start);
      setEndDate(end);
      setStartDateInput(format(start, "yyyy-MM-dd'T'HH:mm"));
      setEndDateInput(format(end, "yyyy-MM-dd'T'HH:mm"));
      setErrors({});
    }
  }, [isOpen, initialStart, initialEnd]);

  const handleStartDateChange = (value: string) => {
    setStartDateInput(value);
    const date = new Date(value);
    if (isValid(date)) {
      setStartDate(date);
      setErrors((prev) => ({ ...prev, start: undefined }));
    } else if (value) {
      setErrors((prev) => ({ ...prev, start: "Invalid date format" }));
    }
  };

  const handleEndDateChange = (value: string) => {
    setEndDateInput(value);
    const date = new Date(value);
    if (isValid(date)) {
      setEndDate(date);
      setErrors((prev) => ({ ...prev, end: undefined }));
    } else if (value) {
      setErrors((prev) => ({ ...prev, end: "Invalid date format" }));
    }
  };

  const handleApply = () => {
    // Validate dates
    if (!isValid(startDate)) {
      setErrors((prev) => ({ ...prev, start: "Please select a valid start date" }));
      return;
    }

    if (!isValid(endDate)) {
      setErrors((prev) => ({ ...prev, end: "Please select a valid end date" }));
      return;
    }

    // Validate that start is before end
    if (startDate > endDate) {
      setErrors({
        start: "Start date must be before end date",
        end: "End date must be after start date",
      });
      return;
    }

    onApply(startDate, endDate);
    onClose();
  };

  const handleQuickSelect = (preset: "today" | "yesterday" | "last7days" | "last30days") => {
    const now = new Date();
    let start: Date;
    let end: Date = endDate;

    switch (preset) {
      case "today":
        start = new Date(now.getFullYear(), now.getMonth(), now.getDate());
        end = new Date(now.getFullYear(), now.getMonth(), now.getDate(), 23, 59, 59);
        break;
      case "yesterday":
        const yesterday = new Date(now);
        yesterday.setDate(yesterday.getDate() - 1);
        start = new Date(yesterday.getFullYear(), yesterday.getMonth(), yesterday.getDate());
        end = new Date(
          yesterday.getFullYear(),
          yesterday.getMonth(),
          yesterday.getDate(),
          23,
          59,
          59
        );
        break;
      case "last7days":
        start = new Date(now);
        start.setDate(start.getDate() - 6);
        start.setHours(0, 0, 0, 0);
        end = new Date(now);
        end.setHours(23, 59, 59, 999);
        break;
      case "last30days":
        start = new Date(now);
        start.setDate(start.getDate() - 29);
        start.setHours(0, 0, 0, 0);
        end = new Date(now);
        end.setHours(23, 59, 59, 999);
        break;
      default:
        return;
    }

    setStartDate(start);
    setEndDate(end);
    setStartDateInput(format(start, "yyyy-MM-dd'T'HH:mm"));
    setEndDateInput(format(end, "yyyy-MM-dd'T'HH:mm"));
    setErrors({});
  };

  return (
    <ResponsiveModal
      open={isOpen}
      onOpenChange={(open) => !open && onClose()}
      title={`Custom ${fieldLabel} Range`}
      maxWidth="md"
      side="bottom"
    >
      <div className="mb-4">
        <p className="text-sm text-gray-600 dark:text-gray-400">
          Select a custom date range to filter {fieldLabel.toLowerCase()}.
        </p>
      </div>

      <div className="space-y-6">
        {/* Quick Select Buttons */}
        <div className="space-y-2">
          <Label className="text-sm font-medium">Quick Select</Label>
          <div className="grid grid-cols-2 gap-2">
            <Button
              variant="outline"
              size="sm"
              onClick={() => handleQuickSelect("today")}
              className="text-xs"
            >
              Today
            </Button>
            <Button
              variant="outline"
              size="sm"
              onClick={() => handleQuickSelect("yesterday")}
              className="text-xs"
            >
              Yesterday
            </Button>
            <Button
              variant="outline"
              size="sm"
              onClick={() => handleQuickSelect("last7days")}
              className="text-xs"
            >
              Last 7 days
            </Button>
            <Button
              variant="outline"
              size="sm"
              onClick={() => handleQuickSelect("last30days")}
              className="text-xs"
            >
              Last 30 days
            </Button>
          </div>
        </div>

        {/* Custom Date Inputs */}
        <div className="space-y-4">
          <div className="space-y-2">
            <Label htmlFor="start-date">Start Date & Time</Label>
            <Input
              id="start-date"
              type="datetime-local"
              value={startDateInput}
              onChange={(e) => handleStartDateChange(e.target.value)}
              className={errors.start ? "border-red-500" : ""}
            />
            {errors.start && <p className="text-sm text-red-500">{errors.start}</p>}
          </div>

          <div className="space-y-2">
            <Label htmlFor="end-date">End Date & Time</Label>
            <Input
              id="end-date"
              type="datetime-local"
              value={endDateInput}
              onChange={(e) => handleEndDateChange(e.target.value)}
              className={errors.end ? "border-red-500" : ""}
            />
            {errors.end && <p className="text-sm text-red-500">{errors.end}</p>}
          </div>
        </div>

        {/* Preview */}
        <div className="bg-card-50 p-3 rounded-md">
          <Label className="text-sm font-medium text-gray-700 dark:text-gray-300">Preview</Label>
          <p className="text-sm text-gray-600 dark:text-gray-400 mt-1">
            {isValid(startDate) && isValid(endDate) ? (
              <>
                {format(startDate, "MMM d, yyyy 'at' h:mm a")} -{" "}
                {format(endDate, "MMM d, yyyy 'at' h:mm a")}
              </>
            ) : (
              "Select valid dates to see preview"
            )}
          </p>
        </div>
      </div>

      <div className="flex justify-end gap-3 mt-6 pt-4 border-t">
        <Button variant="outline" onClick={onClose}>
          Cancel
        </Button>
        <Button
          onClick={handleApply}
          disabled={!isValid(startDate) || !isValid(endDate) || startDate > endDate}
        >
          Apply Filter
        </Button>
      </div>
    </ResponsiveModal>
  );
};
